import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BSC_ORANGE = '#F28C28';
const BSC_GREEN = '#1AA84B';
const LETTER_COLORS = [BSC_ORANGE, '#FFFFFF', BSC_GREEN];

// All apps with the role/access they require.
// `requiredAccess`: a key that must appear in user.app_access (admins bypass).
// `adminOnly`: hide tile for non-admin authenticated users (still shown to anonymous).
const APPS = [
  { id: 'people',       name: 'PEOPLE',  route: '/people',        subtitle: 'Staff & Attendance',       requiredAccess: 'people' },
  { id: 'assets',       name: 'ASSETS',  route: '/assets',        subtitle: 'Asset Tracker',            requiredAccess: 'assets' },
  { id: 'finance',      name: 'FINANCE', route: '/finance',       subtitle: 'Books · GST · Projects & Budgets' },
  { id: 'admin',        name: 'ADMIN',   route: '/admin',         subtitle: 'Users & Company Settings', adminOnly: true },
];

function StripedWordmark({ text }) {
  // Uniform size for ALL tiles, picked so the longest word (EXECUTION = 9 chars)
  // fits comfortably inside the tile box at every breakpoint.
  const cls = 'text-[22px] sm:text-[28px] md:text-[34px] lg:text-[40px]';
  return (
    <div className="flex items-end justify-center leading-none whitespace-nowrap" style={{ fontFamily: "'Monoton', cursive" }}>
      {text.split('').map((ch, i) => {
        const color = LETTER_COLORS[i % LETTER_COLORS.length];
        return (
          <span key={i}
            className={`${cls} transition-colors`}
            style={{ color, textShadow: `0 0 14px ${color}55, 0 0 2px ${color}88` }}>
            {ch}
          </span>
        );
      })}
    </div>
  );
}

function AppTile({ app, onClick, locked, lockReason }) {
  return (
    <button type="button"
      disabled={locked}
      onClick={() => !locked && onClick(app)}
      data-testid={`app-tile-${app.id}`}
      aria-label={`Open ${app.name} app`}
      className={`group relative flex flex-col items-center justify-center gap-5 rounded-2xl border border-white/10 bg-white/[0.02] px-8 py-10 md:py-14 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/30 ${
        locked ? 'opacity-30 cursor-not-allowed' : 'hover:border-white/30 hover:bg-white/[0.05] hover:-translate-y-1 cursor-pointer'
      }`}
      style={{ minHeight: '240px' }}>
      <div className="absolute top-0 left-6 right-6 flex items-center gap-1">
        <div className="h-[2px] flex-1 rounded-full" style={{ backgroundColor: BSC_ORANGE }} />
        <div className="h-[2px] flex-1 rounded-full bg-white" />
        <div className="h-[2px] flex-1 rounded-full" style={{ backgroundColor: BSC_GREEN }} />
      </div>

      <div className="w-full overflow-hidden">
        <StripedWordmark text={app.name} />
      </div>

      <p className="text-white/60 text-[11px] md:text-xs tracking-[0.3em] uppercase">{app.subtitle}</p>

      {locked ? (
        <span className="text-white/40 text-[10px] tracking-[0.25em] uppercase">{lockReason || 'No access'}</span>
      ) : (
        <span className="text-white/50 text-[11px] tracking-[0.25em] uppercase group-hover:text-white transition-colors">
          Click to open &rarr;
        </span>
      )}

      {!locked && (
        <div className="absolute left-6 right-6 bottom-4 h-[2px] w-0 group-hover:w-[calc(100%-48px)] transition-all duration-500"
          style={{ background: `linear-gradient(90deg, ${BSC_ORANGE}, #fff, ${BSC_GREEN})` }} />
      )}
    </button>
  );
}

export default function Launcher() {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const [company, setCompany] = useState({ name: 'BSC Business OS', logo: null });

  useEffect(() => {
    // Public endpoint — anyone can read company branding (no creds required).
    axios.get(`${API}/admin/company`, { withCredentials: true })
      .then((r) => setCompany(r.data))
      .catch(() => { /* fall back to defaults */ });
  }, []);

  const isAuthenticated = !!user && user !== false;
  const isAdmin = isAuthenticated && user?.role === 'admin';
  const access = new Set(user?.app_access || []);

  const visibleApps = APPS.filter((app) => {
    // Admin tile: hidden for authenticated non-admins; visible to anonymous (will trigger login).
    if (app.adminOnly && isAuthenticated && !isAdmin) return false;
    return true;
  });

  // Tile is locked only for an authenticated non-admin who lacks app_access.
  // Anonymous users see all tiles unlocked — clicking goes to /login first.
  // Coming-soon tiles are visible to everyone — clicking shows the placeholder page.
  const lockState = (app) => {
    if (!isAuthenticated) return { locked: false };
    if (isAdmin) return { locked: false };
    if (app.comingSoon) return { locked: false };
    if (app.adminOnly) return { locked: true, reason: 'Admin only' };
    if (!access.has(app.requiredAccess)) return { locked: true, reason: 'No access' };
    return { locked: false };
  };

  const handleOpen = (app) => {
    if (!isAuthenticated) {
      navigate(`/login?next=${encodeURIComponent(app.route)}`);
      return;
    }
    navigate(app.route);
  };

  return (
    <div data-testid="launcher-page"
      className="min-h-screen w-full flex flex-col items-center relative overflow-hidden"
      style={{ backgroundColor: '#000' }}>
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(circle at 20% 20%, rgba(242,140,40,0.08), transparent 45%), radial-gradient(circle at 80% 80%, rgba(26,168,75,0.08), transparent 45%)',
      }} />

      <header className="relative z-10 w-full max-w-6xl px-6 pt-10 md:pt-14 flex flex-col items-center gap-5">
        <img
          src={company.logo || '/bsc-logo.jpeg'}
          alt={company.name || 'BSC'}
          className="w-24 h-24 md:w-28 md:h-28 rounded-2xl shadow-[0_0_40px_rgba(255,255,255,0.06)] select-none object-cover"
          draggable={false}
          data-testid="launcher-company-logo"
        />
        <div className="flex items-center gap-1 w-full max-w-xs">
          <div className="h-[2px] flex-1 rounded-full" style={{ backgroundColor: BSC_ORANGE }} />
          <div className="h-[2px] flex-1 rounded-full bg-white" />
          <div className="h-[2px] flex-1 rounded-full" style={{ backgroundColor: BSC_GREEN }} />
        </div>
        <h1 className="text-white/90 text-base md:text-lg tracking-[0.35em] uppercase text-center"
          style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 500 }}
          data-testid="launcher-company-name">
          {company.name || 'BSC Business OS'}
        </h1>
        <p className="text-white/50 text-[11px] md:text-xs tracking-[0.25em] uppercase text-center">
          {loading
            ? 'Loading…'
            : isAuthenticated
              ? `Welcome, ${user.name} · Select an application`
              : 'Select an application to begin'}
        </p>
      </header>

      <main className="relative z-10 w-full max-w-6xl px-6 py-10 md:py-14 flex-1 flex items-center justify-center">
        <div data-testid="apps-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7 w-full">
          {visibleApps.map((app) => {
            const state = lockState(app);
            return (
              <AppTile key={app.id} app={app} onClick={handleOpen}
                locked={state.locked} lockReason={state.reason} />
            );
          })}
        </div>
      </main>

      <footer className="relative z-10 w-full py-6 flex flex-col items-center gap-2 text-white/30 text-[11px] tracking-widest uppercase">
        {isAuthenticated && (
          <div className="flex justify-center items-center gap-4 mb-1">
            <button onClick={() => navigate('/my-assets')} className="text-white/50 hover:text-white/80" data-testid="launcher-my-assets">My Assets</button>
            <span>·</span>
            <button onClick={() => navigate('/scan-qr')} className="text-white/50 hover:text-white/80" data-testid="launcher-scan-qr">Scan QR</button>
          </div>
        )}
        <div className="flex justify-center items-center gap-4">
          {isAuthenticated ? (
            <button
              onClick={async () => { await logout(); }}
              className="text-white/40 hover:text-white/70"
              data-testid="launcher-logout">
              Logout
            </button>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="text-white/40 hover:text-white/70"
              data-testid="launcher-login">
              Sign in
            </button>
          )}
          <span>·</span>
          <span>&copy; {new Date().getFullYear()} {company.name} · All rights reserved</span>
        </div>
        <div className="text-white/40 text-[10px] tracking-[0.25em]" data-testid="developer-credit">
          Developed by <span className="text-white/60">Business Science Cohort Technology — UAE</span> &amp; <span className="text-white/60">VJC</span>
        </div>
      </footer>
    </div>
  );
}
